/**
 * useShellWeaponGates - what the armed weapon's own rules say about the
 * next shot, before anybody rolls.
 *
 * - INDIRECT (v8.198.5): the mortar arc. When the map says the target is
 *   out of sight, an Indirect weapon may still fire - at -10; under a
 *   minimum range the shot is refused outright. Standoff(X) shares the gate
 *   via `minimumRangeOf` (v8.280.0).
 * - MELEE REACH (plan map-display, D): a melee weapon out of reach of the
 *   target, by the map, is refused - strict, footprint to footprint.
 * - CYCLING(X) (v8.289.0): the capacitor recharges by ROUNDS. Local,
 *   per-client state (the aim precedent) - it gates this client's own
 *   trigger and ticks down when the tracker's round advances, or a beat
 *   of time passes outside the rounds (`frame.beats`).
 *
 * Returns `{ indirectFire: { noLoS, blockReason }, cyclingBlockReason,
 * attackBlockedReason, attackModFinal, startCooldown(firedSpecials) }` -
 * the modifier with Indirect's blind -10 folded in, so the roll and the
 * forecast agree. Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { cyclingRounds, fireModeRefusal, tickCooldowns } from '../engine/fireModes';
import { hasWS } from '../engine/weaponSpecials';
import { minimumRangeOf } from '../engine/attackCalc';
import { reachReason, weaponReach } from '../engine/battlefield/reach';

/** The cooldown's key: the weapon itself, or its plate on the scratch rack. */
export function cooldownKey(effectiveSlot, selectedSlot) {
  return effectiveSlot?.weaponId ?? 'slot-' + selectedSlot;
}

/** The Indirect / minimum-range answer for this weapon against this measurement. */
export function indirectFireOf(specialRules, measurement) {
  const minRange = minimumRangeOf(specialRules);
  const noLoS = hasWS(specialRules, 'Indirect') && !!measurement?.blocked;
  const blockReason =
    measurement && minRange > 0 && measurement.metres < minRange
      ? `Minimum range: the target is inside ${minRange}m - the shot cannot come down that close.`
      : null;
  return { noLoS, blockReason };
}

/** The trigger's refusal while the capacitor recharges, or null. */
export function cyclingReason(left) {
  return left > 0 ? `Cycling: the weapon is recharging - ${left} more round${left > 1 ? 's' : ''}.` : null;
}

/** The cooldowns, ticked down once each time the count of rounds and beats moves. */
function useCooldowns(trackerRound) {
  const [weaponCooldowns, setWeaponCooldowns] = useState({});
  const prevRoundRef = useRef(trackerRound);
  useEffect(() => {
    if (trackerRound === prevRoundRef.current) return;
    // One tick for each round or beat the count rose by. A count that fell (the tracker switched
    // off, an undo) is one change, as it always was.
    const ticks = Math.max(1, trackerRound - prevRoundRef.current);
    prevRoundRef.current = trackerRound;
    setWeaponCooldowns((m) => {
      let next = m;
      for (let i = 0; i < ticks; i++) next = tickCooldowns(next);
      return next;
    });
  }, [trackerRound]);
  return [weaponCooldowns, setWeaponCooldowns];
}

/**
 * @param {object} args
 * @param {object|null} args.effectiveSlot - the armed weapon, overrides applied
 * @param {number|null} args.selectedSlot
 * @param {object|null} args.measurement - the live map's measurement to the target
 * @param {number|null} [args.reachGap] - the live map's gap to the target, footprint to footprint (null: no map)
 * @param {number} args.trackerRound - the turn tracker's round (0 when off) plus the beats of time passed
 * @param {number} args.attackMod - the toggles' and custom mods' total
 */
export default function useShellWeaponGates({
  effectiveSlot,
  selectedSlot,
  measurement,
  reachGap = null,
  trackerRound,
  attackMod,
  actorConds,
  fireMode,
}) {
  const indirectFire = useMemo(
    () => indirectFireOf(effectiveSlot?.specialRules || [], measurement),
    [effectiveSlot, measurement],
  );
  const [weaponCooldowns, setWeaponCooldowns] = useCooldowns(trackerRound);
  const cyclingBlockReason = useMemo(
    () => cyclingReason(weaponCooldowns[cooldownKey(effectiveSlot, selectedSlot)] || 0),
    [effectiveSlot, selectedSlot, weaponCooldowns],
  );
  // The shot spends the capacitor. A bare classic Recharge reads as Cycling(1).
  const startCooldown = useCallback(
    (firedSpecials) => {
      const cyc = cyclingRounds(firedSpecials || []);
      if (!(cyc > 0)) return;
      const key = cooldownKey(effectiveSlot, selectedSlot);
      setWeaponCooldowns((m) => ({ ...m, [key]: cyc }));
    },
    [effectiveSlot, selectedSlot, setWeaponCooldowns],
  );
  // STRICT MELEE REACH (plan map-display, D): a melee weapon strikes only across a gap it reaches; the map says.
  const meleeReachReason =
    effectiveSlot?.weaponType === 'melee' ? reachReason(reachGap, weaponReach(effectiveSlot)) : null;
  return {
    indirectFire,
    cyclingBlockReason,
    meleeReachReason,
    // Pinned fires single shots only, a runner full auto only (2026-10-26).
    attackBlockedReason:
      indirectFire.blockReason ||
      meleeReachReason ||
      cyclingBlockReason ||
      fireModeRefusal(actorConds, fireMode, effectiveSlot),
    attackModFinal: attackMod + (indirectFire.noLoS ? -10 : 0),
    startCooldown,
  };
}
